import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { MetaMaskStatus, YatModalSideSheet } from '@pkg/components';
import { AcceptTerms } from '@pkg/components/NFT/ArtifactMintPanel/ArtifactSteps/AcceptTerms';
import { ArtifactMintedFeedback } from '@pkg/components/NFT/ArtifactMintPanel/ArtifactSteps/ArtifactMintedFeedback';
import { ArtifactMintComplete } from '@pkg/components/NFT/ArtifactMintPanel/ArtifactSteps/Complete';
import { Loading } from '@pkg/components/NFT/ArtifactMintPanel/ArtifactSteps/Loading';
import { MintArtifact } from '@pkg/components/NFT/ArtifactMintPanel/ArtifactSteps/MintArtifact';
import { SelectArtifact } from '@pkg/components/NFT/ArtifactMintPanel/ArtifactSteps/SelectArtifact';
import { useAppDispatch, useAppSelector, useMetaMask } from '@pkg/hooks';

import { ArtifactMintSteps } from '@app/client/common/structs/nft/artifacts';
import {
    setArtifactMintPanelOpen,
    setArtifactMintPanelStep,
    setSelectedArtifactMintDisplay,
} from '@app/store/actions/artifacts';

// TODO: Restructure these files so they can share the sidebar scss ?
import '../NftSidePanel/NftSidePanel.scss';

import './ArtifactMintPanel.scss';

export const YatArtifactMintPanel = () => {
    const dispatch = useAppDispatch();
    const { artifactMintPanelOpen, artifactMintPanelStep } = useAppSelector((state) => state.artifacts);

    const { isMetaMaskConnected } = useMetaMask();
    const [currentStep, setCurrentStep] = useState<string | null>(artifactMintPanelStep);

    useEffect(() => {
        if (artifactMintPanelStep) {
            setCurrentStep(artifactMintPanelStep);
        }
    }, [artifactMintPanelStep]);

    // Load right component based on current step
    const renderStep = useMemo(() => {
        switch (currentStep) {
            case ArtifactMintSteps.Complete:
                return <ArtifactMintComplete />;
            case ArtifactMintSteps.Loading:
                return <Loading />;
            case ArtifactMintSteps.ArtifactMintedFeedback:
                return <ArtifactMintedFeedback />;
            case ArtifactMintSteps.AcceptTerms:
                return <AcceptTerms />;
            case ArtifactMintSteps.Introduction:
                return <MintArtifact />;
            case ArtifactMintSteps.Select:
            default:
                return <SelectArtifact />;
        }
    }, [currentStep]);

    const onClose = useCallback(() => {
        dispatch(setArtifactMintPanelOpen(false));
        dispatch(setArtifactMintPanelStep(ArtifactMintSteps.Select));
        dispatch(setSelectedArtifactMintDisplay(null));
    }, [dispatch]);

    const mmSteps: string[] = [
        ArtifactMintSteps.Introduction,
        ArtifactMintSteps.AcceptTerms,
        ArtifactMintSteps.Complete,
    ];
    const showMetamaskStatus = isMetaMaskConnected && mmSteps.includes(currentStep || '');

    const topBarStatus = useMemo(() => {
        if (showMetamaskStatus) {
            return (
                <MetaMaskStatus
                    classes={['yat-nft-sidebar__metamask-status yat-nft-sidebar__metamask-status--mobile ']}
                />
            );
        } else {
            return undefined;
        }
    }, [showMetamaskStatus]);

    return (
        <YatModalSideSheet
            classes={'nft-sidebar nft-sidebar--artifact'}
            modalLabelId="yat-artifact-panel"
            closeCallback={() => onClose()}
            open={artifactMintPanelOpen}
            header={topBarStatus}
            side="right"
        >
            <div
                id="yat-artifact-panel"
                key={`artifact-panel-content`}
                className={`yat-artifact-sidebar yat-nft-sidebar`}
            >
                {renderStep}
                {showMetamaskStatus && (
                    <MetaMaskStatus
                        classes={['yat-nft-sidebar__metamask-status yat-nft-sidebar__metamask-status--desktop ']}
                    />
                )}
            </div>
        </YatModalSideSheet>
    );
};
